import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { LiaCartPlusSolid } from 'react-icons/lia';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { IoIosArrowBack } from 'react-icons/io';
import toast from 'react-hot-toast';
import { ProductContext } from '../context/ProductContext';
import StarRating from '../component/StarRating';
import { ItemSkeleton } from '../component/Skeleton';
import CardComponent from '../component/card';

function Item() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addCartItem, wishlist, toggleWishlist } = useContext(ProductContext);

  const [item, setItem] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImage(0);
    setQuantity(1);

    fetch(`https://dummyjson.com/products/${id}`)
      .then((res) => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then((data) => {
        setItem(data);
        setLoading(false);
        return fetch(`https://dummyjson.com/products/category/${data.category}?limit=6`);
      })
      .then((res) => res && res.json())
      .then((data) => {
        if (data) setRelated(data.products.filter((p) => String(p.id) !== id));
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [id]);

  const backButton = (
    <button
      onClick={() => navigate(-1)}
      className="mb-4 flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-brand-700"
    >
      <IoIosArrowBack /> Back
    </button>
  );

  if (loading) return <ItemSkeleton />;

  if (notFound || !item) {
    return (
      <div>
        {backButton}
        <div className="flex flex-col items-center gap-3 py-20 text-center">
          <h2 className="text-xl font-bold text-gray-900">Product not found</h2>
          <p className="text-gray-500">This product may have been removed.</p>
          <Link to="/" className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">
            Back to shop
          </Link>
        </div>
      </div>
    );
  }

  const sellingPrice = item.price - (item.price * item.discountPercentage) / 100;
  const inWishlist = wishlist.some((p) => p.id === item.id);

  const addToCart = () => {
    addCartItem(item, quantity);
    toast.success('Item added to cart');
  };

  const buyNow = () => {
    toast.success('Purchase feature is under development!');
  };

  return (
    <div className="space-y-10">
      {backButton}
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <div>
          <div className="aspect-square w-full overflow-hidden rounded-xl border border-gray-200 bg-white">
            <img src={item.images?.[activeImage] || item.thumbnail} alt={item.title} className="h-full w-full object-contain" />
          </div>
          {item.images && item.images.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto scrollbar-hide">
              {item.images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(index)}
                  className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${
                    activeImage === index ? 'border-brand-600' : 'border-gray-200'
                  }`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {item.brand && (
            <span className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {item.brand}
            </span>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{item.title}</h1>

          <div className="flex items-center gap-2">
            <StarRating rating={item.rating} />
            <span className="text-sm text-gray-500">
              {item.rating} · {item.reviews?.length || 0} reviews
            </span>
          </div>

          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-extrabold text-gray-900">${sellingPrice.toFixed(2)}</span>
            {item.discountPercentage > 0 && (
              <>
                <span className="text-lg text-gray-400 line-through">${item.price.toFixed(2)}</span>
                <span className="rounded bg-rose-100 px-2 py-0.5 text-sm font-semibold text-rose-700">
                  -{item.discountPercentage.toFixed(0)}%
                </span>
              </>
            )}
          </div>

          <p className="text-sm leading-relaxed text-gray-600">{item.description}</p>

          <ul className="grid grid-cols-1 gap-1 text-sm text-gray-600 sm:grid-cols-2">
            <li>
              Stock:{' '}
              <span className={item.stock > 10 ? 'text-emerald-600' : 'text-rose-600'}>
                {item.availabilityStatus || `${item.stock} left`}
              </span>
            </li>
            {item.warrantyInformation && <li>Warranty: {item.warrantyInformation}</li>}
            {item.shippingInformation && <li>Shipping: {item.shippingInformation}</li>}
            {item.returnPolicy && <li>Returns: {item.returnPolicy}</li>}
          </ul>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Quantity</span>
            <div className="flex items-center rounded-lg border border-gray-300">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-50"
              >
                -
              </button>
              <span className="w-10 text-center text-sm font-medium">{quantity}</span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-50"
              >
                +
              </button>
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Total: <span className="font-semibold text-gray-900">${(sellingPrice * quantity).toFixed(2)}</span>
          </p>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={addToCart}
              className="flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <LiaCartPlusSolid size={18} /> Add to Cart
            </button>
            <button
              onClick={buyNow}
              className="flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
            >
              <AiOutlineThunderbolt size={18} /> Buy Now
            </button>
            <button
              onClick={() => toggleWishlist(item)}
              aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
              className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50"
            >
              {inWishlist ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>
        </div>
      </div>

      {item.reviews && item.reviews.length > 0 && (
        <div>
          <h2 className="mb-4 text-lg font-bold text-gray-900">Customer Reviews</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {item.reviews.map((review, index) => (
              <div key={index} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">{review.reviewerName}</span>
                  <StarRating rating={review.rating} size={12} />
                </div>
                <p className="mt-2 text-sm text-gray-600">{review.comment}</p>
                <p className="mt-1 text-xs text-gray-400">{new Date(review.date).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {related.length > 0 && (
        <div>
          <h2 className="mb-4 text-lg font-bold text-gray-900">Related Products</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((p) => (
              <CardComponent key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Item;
